/* eslint-disable max-len */

import { shuffle } from 'lodash';
import { mapGetters } from 'vuex';
import { swiper, swiperSlide } from 'vue-awesome-swiper';
import store from '@/store';

const TRACK_RESIZE_MIN_HEIGHT = 120;
const SWIPER_LAYOUT_REFRESH_DELAYS = [0, 120, 400];
const PODCAST_RESIZE_EVENT = 'cms:podcast-resize';
const PODCAST_LAYOUT_REFRESH_EVENT = 'cms:podcast-layout-refresh';
const STORY_COLLAPSED_HEIGHTS = {
  appleShow: { desktop: 450, mobile: 450 },
  appleEpisode: { desktop: 175, mobile: 175 },
  default: { desktop: 450, mobile: 450 },
};

export const PODCAST = {
  ITUNES: 'itunes',
  SOUNDCLOUD: 'soundcloud',
  BACKTRACKS: 'backtracks',
  POLITICAN_TRADER: 'politicantrader',
  APPLE: 'apple',
  STITCHER: 'stitcher',
};

export default {
  components: {
    swiper,
    swiperSlide,
  },
  props: {
    title: {
      type: String,
      default: 'Featured Podcasts',
    },
    items: {
      type: Array,
      default: () => [],
    },
    theme: {
      type: String,
      default: 'default',
    },
    forceLayoutMode: {
      type: String,
      default: '',
    },
  },
  data() {
    const isLayoutMobile = this.forceLayoutMode === 'mobile'
      ? true
      : (this.forceLayoutMode === 'desktop'
        ? false
        : store.getters['app/isLayoutMobile']);
    const defaultTracks = shuffle([
      { id: 'id1437934639', type: PODCAST.APPLE },
      { id: 'id1496616116', type: PODCAST.APPLE },
      { id: 'id1510570053', type: PODCAST.APPLE },
      { id: 'id1136826256', type: PODCAST.APPLE },
      // { id: '505391', type: PODCAST.STITCHER },
      // { id: '', type: PODCAST.POLITICAN_TRADER },
      // { id: 'https://soundcloud.com/stratfortalks', type: PODCAST.SOUNDCLOUD },
      // { id: 'stratfor/stratfor-quick-take/m/a-second-u-s-north-korea-summit', type: PODCAST.BACKTRACKS },
      // { id: '1437934639', type: PODCAST.ITUNES },
    ]);
    return {
      PODCAST,
      activeIndex: 0,
      isPlaying: true,
      providerTrackHeights: {},
      resizeObserver: null,
      layoutRefreshTimeouts: [],
      tracks: this.items.length > 0 ? this.items : (this.theme === 'story' ? [] : defaultTracks),
      swiperOptions: {
        loop: false,
        autoHeight: true,
        autoplay: (this.theme === 'story' || isLayoutMobile) ? false : 5000,
        direction: 'horizontal',
        spaceBetween: 0,
        onTransitionStart: (swp) => {
          const nextIndex = swp.activeIndex;
          const didSlideChange = nextIndex !== this.activeIndex;
          this.activeIndex = nextIndex;
          if (didSlideChange) {
            this.queueSwiperLayoutRefresh({ speed: 0 });
          }
        },
      },
    };
  },
  computed: {
    ...mapGetters('app', ['isLayoutMobile']),
    isLayoutMobileView() {
      if (this.forceLayoutMode === 'mobile') {
        return true;
      }
      if (this.forceLayoutMode === 'desktop') {
        return false;
      }
      return this.isLayoutMobile;
    },
    isStoryTheme() {
      return this.theme === 'story';
    },
    classModifiers() {
      return {
        podcasts: true,
        [`podcasts--${this.theme}`]: this.theme,
      };
    },
    activeTrack() {
      return this.tracks[this.activeIndex] || this.tracks[0] || null;
    },
    height() {
      if (this.isStoryTheme) {
        return 450;
      }
      return this.isLayoutMobileView ? 450 : 400;
    },
    styleItunesIframe() {
      if (this.isLayoutMobileView) {
        return 'overflow-x:hidden;overflow-y:hidden;height:300px;border:0px';
      }
      return 'overflow-x:hidden;overflow-y:hidden;height:300px;border:0px;width:100%';
    },
    showArrows() {
      return this.tracks.length > 1;
    },
    isPrevArrowDisabled() {
      return this.activeIndex === 0;
    },
    isNextArrowDisabled() {
      return this.activeIndex === (this.tracks.length - 1);
    },
    prevArrowModifiers() {
      return {
        'podcasts__header-arrow': true,
        'podcasts__header-arrow--left': true,
        'podcasts__header-arrow--disabled': this.isPrevArrowDisabled,
      };
    },
    nextArrowModifiers() {
      return {
        'podcasts__header-arrow': true,
        'podcasts__header-arrow--right': true,
        'podcasts__header-arrow--disabled': this.isNextArrowDisabled,
      };
    },
  },
  methods: {
    getResponsiveHeight(heightMap) {
      return this.isLayoutMobileView ? heightMap.mobile : heightMap.desktop;
    },
    getSwiperInstance() {
      return this.$refs.swiper && this.$refs.swiper.swiper ? this.$refs.swiper.swiper : null;
    },
    clearPendingLayoutRefreshes() {
      for (const timeoutId of this.layoutRefreshTimeouts) {
        clearTimeout(timeoutId);
      }
      this.layoutRefreshTimeouts = [];
    },
    queueSwiperLayoutRefresh({ speed = 0 } = {}) {
      this.clearPendingLayoutRefreshes();
      for (const delay of SWIPER_LAYOUT_REFRESH_DELAYS) {
        const timeoutId = setTimeout(() => {
          this.$nextTick(() => {
            this.updateSwiperLayout(speed);
          });
        }, delay);
        this.layoutRefreshTimeouts.push(timeoutId);
      }
    },
    emitResizeSignal() {
      window.dispatchEvent(new Event(PODCAST_RESIZE_EVENT));
    },
    isVisibleInLayout() {
      return !!(this.$el && this.$el.offsetParent !== null);
    },
    isTrackEpisode(track = {}) {
      return track.type === PODCAST.APPLE && track.id && track.id.includes('?i=');
    },
    getTrackKey(track = {}, index = 0) {
      return `${track.type || 'unknown'}:${track.id || index}`;
    },
    getMeasuredTrackHeight(track, index) {
      const key = this.getTrackKey(track, index);
      const measured = this.providerTrackHeights[key];
      if (typeof measured !== 'number' || measured < TRACK_RESIZE_MIN_HEIGHT) {
        return null;
      }
      return measured;
    },
    getCollapsedStoryHeight(track) {
      if (track && track.type === PODCAST.APPLE) {
        return this.getResponsiveHeight(this.isTrackEpisode(track) ? STORY_COLLAPSED_HEIGHTS.appleEpisode : STORY_COLLAPSED_HEIGHTS.appleShow);
      }
      return this.getResponsiveHeight(STORY_COLLAPSED_HEIGHTS.default);
    },
    getTrackHeight(track, index) {
      if (!this.isStoryTheme) {
        return this.height;
      }
      const collapsedHeight = this.getCollapsedStoryHeight(track);
      const measuredHeight = this.getMeasuredTrackHeight(track, index);
      return measuredHeight ? Math.max(collapsedHeight, measuredHeight) : collapsedHeight;
    },
    getTrackFrameStyle(track, index) {
      return {
        width: '100%',
        minHeight: `${this.getTrackHeight(track, index)}px`,
        height: `${this.getTrackHeight(track, index)}px`,
      };
    },
    getActiveTrackHeight() {
      if (!this.isStoryTheme || !this.activeTrack) {
        return null;
      }
      return this.getTrackHeight(this.activeTrack, this.activeIndex);
    },
    syncStoryThemeFrameHeights() {
      if (!this.isStoryTheme || !this.$el) {
        return;
      }
      const activeHeight = this.getActiveTrackHeight();
      if (!activeHeight) {
        return;
      }
      const activeHeightPx = `${activeHeight}px`;
      const container = this.$el.querySelector('.swiper-container');
      const wrapper = this.$el.querySelector('.swiper-wrapper');
      const slides = this.$el.querySelectorAll('.swiper-slide');

      if (container) {
        container.style.setProperty('height', activeHeightPx, 'important');
      }
      if (wrapper) {
        wrapper.style.setProperty('height', activeHeightPx, 'important');
      }

      for (const [index, slide] of Array.from(slides).entries()) {
        if (index === this.activeIndex) {
          slide.style.setProperty('height', activeHeightPx, 'important');
          const iframe = slide.querySelector('iframe');
          if (iframe) {
            iframe.style.setProperty('height', activeHeightPx, 'important');
          }
        } else {
          slide.style.removeProperty('height');
        }
      }
    },
    updateSwiperLayout(speed = 0) {
      const swiperInstance = this.getSwiperInstance();
      if (!swiperInstance) {
        return;
      }
      if (typeof swiperInstance.update === 'function') {
        swiperInstance.update();
      }
      if (typeof swiperInstance.updateSize === 'function') {
        swiperInstance.updateSize();
      }
      if (typeof swiperInstance.updateSlides === 'function') {
        swiperInstance.updateSlides();
      }
      if (typeof swiperInstance.updateAutoHeight === 'function') {
        swiperInstance.updateAutoHeight(speed);
      }
      this.syncStoryThemeFrameHeights();
    },
    onTrackFrameLoad() {
      this.queueSwiperLayoutRefresh({ speed: 0 });
      this.$nextTick(() => {
        this.syncStoryThemeFrameHeights();
      });
      this.emitResizeSignal();
    },
    handleExternalLayoutRefresh() {
      if (!this.isVisibleInLayout()) {
        return;
      }
      this.queueSwiperLayoutRefresh({ speed: 0 });
      this.$nextTick(() => {
        this.syncStoryThemeFrameHeights();
        this.emitResizeSignal();
      });
    },
    extractHeightFromMessage(data) {
      if (typeof data === 'number' && data >= TRACK_RESIZE_MIN_HEIGHT) {
        return data;
      }
      if (typeof data === 'string') {
        try {
          return this.extractHeightFromMessage(JSON.parse(data));
        } catch (e) {
          const match = data.match(/height\s*[:=]\s*"?(\d{2,4})"?/i);
          if (match) {
            return parseInt(match[1], 10);
          }
          return null;
        }
      }
      if (typeof data !== 'object' || data === null) {
        return null;
      }
      const candidateKeys = ['height', 'frameHeight', 'playerHeight'];
      for (const key of candidateKeys) {
        if (typeof data[key] === 'number' && data[key] >= TRACK_RESIZE_MIN_HEIGHT) {
          return data[key];
        }
      }
      if (typeof data.data !== 'undefined') {
        return this.extractHeightFromMessage(data.data);
      }
      return null;
    },
    handleProviderMessage(event) {
      if (!this.isStoryTheme || !event || !event.origin || event.origin.indexOf('podcasts.apple.com') === -1) {
        return;
      }
      const activeTrack = this.activeTrack;
      if (!activeTrack || activeTrack.type !== PODCAST.APPLE) {
        return;
      }
      const nextHeight = this.extractHeightFromMessage(event.data);
      if (!nextHeight || nextHeight < TRACK_RESIZE_MIN_HEIGHT) {
        return;
      }
      const key = this.getTrackKey(activeTrack, this.activeIndex);
      this.$set(this.providerTrackHeights, key, Math.ceil(nextHeight));
      this.queueSwiperLayoutRefresh({ speed: 0 });
      this.$nextTick(() => {
        this.syncStoryThemeFrameHeights();
      });
      this.emitResizeSignal();
    },
    stopSwiper() {
      this.isPlaying = false;
      if (this.$refs.swiper && this.$refs.swiper.swiper) {
        this.$refs.swiper.swiper.stopAutoplay();
      }
    },
    startSwiper() {
      this.isPlaying = true;
      if (this.$refs.swiper && this.$refs.swiper.swiper) {
        this.$refs.swiper.swiper.startAutoplay();
      }
    },
    getPoliticanTraderUrl() {
      return '//html5-player.libsyn.com/embed/destination/id/1822847/height/300/theme/custom/thumbnail/yes/direction/backward/no-cache/true/render-playlist/yes/custom-color/2607ab/';
    },
    getBacktracksUrl(id) {
      return `https://player.backtracks.fm/${id}`;
    },
    getSoundCloudUrl(track) {
      return `https://w.soundcloud.com/player/?url=${track}&auto_play=false&show_artwork=false`;
    },
    getItunesUrl(ids) {
      const width = this.isLayoutMobileView ? '290' : '476';
      const query = `?c=us&d=Test&t=Test&m=podcast&e=podcast&w=${width}&h=300&ids=${ids}&wt=playlist`;
      return `//widgets.itunes.apple.com/widget.html${query}`;
    },
    getAppleUrl(id) {
      const separator = id.includes('?') ? '&' : '?';
      return `https://embed.podcasts.apple.com/us/podcast/star-spangled-gamblers/${id}${separator}itsct=podcast_box&amp;itscg=30200`;
    },
    getStitcherUrl(id) {
      return `https://www.stitcher.com/embed/${id}`;
    },
    prevSlide() {
      if (!this.isPrevArrowDisabled && this.$refs.swiper && this.$refs.swiper.swiper) {
        this.$refs.swiper.swiper.slidePrev();
      }
    },
    nextSlide() {
      if (!this.isNextArrowDisabled && this.$refs.swiper && this.$refs.swiper.swiper) {
        this.$refs.swiper.swiper.slideNext();
      }
    },
  },
  watch: {
    isLayoutMobileView() {
      this.queueSwiperLayoutRefresh({ speed: 0 });
      this.$nextTick(() => {
        this.syncStoryThemeFrameHeights();
      });
      this.emitResizeSignal();
    },
    tracks() {
      this.queueSwiperLayoutRefresh({ speed: 0 });
      this.$nextTick(() => {
        this.syncStoryThemeFrameHeights();
      });
      this.emitResizeSignal();
    },
  },
  mounted() {
    window.addEventListener('message', this.handleProviderMessage);
    window.addEventListener('resize', this.onTrackFrameLoad);
    window.addEventListener(PODCAST_LAYOUT_REFRESH_EVENT, this.handleExternalLayoutRefresh);

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this.queueSwiperLayoutRefresh({ speed: 0 });
      });
      this.resizeObserver.observe(this.$el);
    }

    this.queueSwiperLayoutRefresh({ speed: 0 });
    this.$nextTick(() => {
      this.syncStoryThemeFrameHeights();
    });
  },
  beforeDestroy() {
    window.removeEventListener('message', this.handleProviderMessage);
    window.removeEventListener('resize', this.onTrackFrameLoad);
    window.removeEventListener(PODCAST_LAYOUT_REFRESH_EVENT, this.handleExternalLayoutRefresh);
    this.clearPendingLayoutRefreshes();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  },
};



// WEBPACK FOOTER //
// ./src/components/home/home-old/podcasts/podcasts.js